import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    LogNotification,
    gameServiceAcceptChallenge,
    gameServiceCancelChallenge,
    gameServiceGetChallenge,
} from '@/api';
import { Notification as ToastNotification } from '@/shared/ui/notifications/notifications';
import styles from './styles.module.scss';
import { Button } from '@igo/ui';

type Props = {
    notification: LogNotification;
    onMarkAsRead: (id: string) => void;
    onAccepted: (gameId: string) => void;
};

const CHALLENGE_UNAVAILABLE_CODE = 14;

export const ChallengeNotificationActions = ({ notification, onMarkAsRead, onAccepted }: Props) => {
    const [isPending, setIsPending] = useState(false);
    const { t } = useTranslation('server', { keyPrefix: 'games_page' });
    const { t: tNotifications } = useTranslation('global', { keyPrefix: 'notifications' });

    const handleUnavailable = () => {
        toast(<ToastNotification type="warning" text={tNotifications('challengeUnavailable')} />);
        if (notification.id) onMarkAsRead(notification.id);
    };

    const handleAccept = async () => {
        if (!notification.parentId || isPending) return;
        setIsPending(true);

        const response = await gameServiceAcceptChallenge({ path: { id: notification.parentId } });
        if (response.error) {
            setIsPending(false);
            if (response.error.code === CHALLENGE_UNAVAILABLE_CODE) {
                handleUnavailable();
                return;
            }
            toast(<ToastNotification type="warning" text={response.error.message ?? t('errorAcceptChallenge')} />);
            return;
        }

        if (notification.id) onMarkAsRead(notification.id);
        if (response.data.gameId) onAccepted(response.data.gameId);
    };

    const handleReject = async () => {
        if (!notification.parentId || isPending) return;
        setIsPending(true);

        const challenge = await gameServiceGetChallenge({ path: { id: notification.parentId } });
        if (challenge.error) {
            setIsPending(false);
            toast(<ToastNotification type="warning" text={challenge.error.message ?? t('errorLoadStats')} />);
            return;
        }
        if (!challenge.data?.id || challenge.data.acceptedAt || challenge.data.doneAt) {
            setIsPending(false);
            handleUnavailable();
            return;
        }

        const response = await gameServiceCancelChallenge({ path: { id: notification.parentId } });
        if (response.error) {
            setIsPending(false);
            if (response.error.code === CHALLENGE_UNAVAILABLE_CODE) {
                handleUnavailable();
                return;
            }
            toast(<ToastNotification type="warning" text={response.error.message ?? t('errorCancelChallenge')} />);
            return;
        }

        if (notification.id) onMarkAsRead(notification.id);
    };

    return (
        <div
            className={styles.challengeActions}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
        >
            <Button variant="secondary" disabled={isPending} onClick={handleAccept}>
                {t('Accept')}
            </Button>
            <Button variant="thirdy" disabled={isPending} onClick={handleReject}>
                {t('Reject')}
            </Button>
        </div>
    );
};
